/**
 * Hook for smooth number transitions using Framer Motion.
 * When a computed value changes, it animates from the old value to the new one.
 */

import { useEffect, useRef, useState } from 'react';
import { animate } from 'framer-motion';

interface UseAnimatedValueOptions {
  /** Spring stiffness (default: 100) */
  stiffness?: number;
  /** Spring damping (default: 20) */
  damping?: number;
  /** Duration in seconds — overrides spring physics if set */
  duration?: number;
  /** Decimal places for the animated value */
  decimals?: number;
}

/**
 * Animates a number from its previous value to the current value.
 * Returns the animated (in-progress) value.
 *
 * Usage:
 * ```tsx
 * const animatedPnL = useAnimatedValue(pnlResult.monthlyPnL);
 * return <span>{formatCurrency(animatedPnL)}</span>
 * ```
 */
export function useAnimatedValue(
  targetValue: number,
  options: UseAnimatedValueOptions = {}
): number {
  const {
    stiffness = 100,
    damping = 20,
    duration,
    decimals = 0,
  } = options;

  const [displayValue, setDisplayValue] = useState(targetValue);
  const prevValueRef = useRef(targetValue);

  useEffect(() => {
    const from = prevValueRef.current;
    const to = targetValue;
    prevValueRef.current = to;

    // Skip animation for same value
    if (from === to) return;

    const controls = animate(from, to, {
      ...(duration
        ? { duration, ease: 'easeOut' }
        : { type: 'spring', stiffness, damping }
      ),
      onUpdate: (latest) => {
        setDisplayValue(
          decimals > 0 ? parseFloat(latest.toFixed(decimals)) : Math.round(latest)
        );
      },
    });

    return () => controls.stop();
  }, [targetValue, stiffness, damping, duration, decimals]);

  return displayValue;
}
